import { useStorage } from '@vueuse/core'
import { computed, ref } from 'vue'
import { OfflineStatusEnum } from '~/lib/switchings'
import type { VoltageLevelEnum } from '~/composables/coloring/constants'
import type { EquipmentTypeEnum, SwitchingCategoryTypeEnum } from '@gridcon-energy/schaltungstool-backend'
import { EquipmentNetworkStateEnum } from '~/lib/equipments'
import type { EquipmentStatusEnum } from '~/lib/equipments'

export type NetworkSVGStorage = {
  createdAt?: number
  showLegend: boolean
  showSVGDetails: boolean
}

const inMemory = ref({
  activityId: null as number | null,
  disabledLegendKeys: [] as FilterKey[],
})

export enum FilterTypeEnum {
  equipment_status = 'equipment_status',
  network_state = 'network_state',
  offline_range = 'offline_range',
  project = 'project',
  voltage = 'voltage',
  category = 'category',
  equipment_type = 'equipment_type',
}

type FilterTypeValue<T extends FilterTypeEnum> = T extends FilterTypeEnum.equipment_status
  ? EquipmentStatusEnum | null
  : T extends FilterTypeEnum.network_state
    ? EquipmentNetworkStateEnum
    : T extends FilterTypeEnum.offline_range
      ? OfflineStatusEnum
      : T extends FilterTypeEnum.project
        ? number | 'default' // project id
        : T extends FilterTypeEnum.voltage
          ? VoltageLevelEnum
          : T extends FilterTypeEnum.category
            ? SwitchingCategoryTypeEnum
            : T extends FilterTypeEnum.equipment_type
              ? EquipmentTypeEnum
              : string

export type FilterKey<
  T extends FilterTypeEnum = FilterTypeEnum,
  V extends FilterTypeValue<T> = FilterTypeValue<T>,
> = `${T}:${V}`

export function generateFilterKey<T extends FilterTypeEnum, V extends FilterTypeValue<T>>(
  type: T,
  value: V,
): FilterKey<T, V> {
  return `${type}:${value}`
}

export function useNetworkSVG(activityId: number) {
  const inStorage = useStorage<NetworkSVGStorage>(`${activityId}.network-svg`, {
    createdAt: new Date().getTime(),
    showLegend: false,
    showSVGDetails: false,
  })
  if (inMemory.value.activityId !== activityId) {
    inMemory.value.activityId = activityId
    inMemory.value.disabledLegendKeys = []
  }

  // Computed Set for performant lookups
  const disabledKeys = computed(() => new Set(inMemory.value.disabledLegendKeys))

  // Toggle a legend key filter
  function toggleLegendKey(key: FilterKey) {
    const keys = new Set(inMemory.value.disabledLegendKeys)
    if (keys.has(key)) {
      keys.delete(key)
    } else {
      keys.add(key)
    }
    // Re-assignment to trigger reactivity without deep watcher
    inMemory.value.disabledLegendKeys = Array.from(keys)
  }

  // Check if a legend key is disabled
  function isLegendKeyDisabled(key: FilterKey): boolean {
    return disabledKeys.value.has(key)
  }

  // Clear all disabled keys
  function clearDisabledKeys() {
    inMemory.value.disabledLegendKeys = []
  }

  return {
    inMemory,
    inStorage,
    disabledKeys,
    toggleLegendKey,
    isLegendKeyDisabled,
    clearDisabledKeys,
  }
}
